import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {
  X5LessonSnapshot,
  X5LessonTypes,
  X6PageSnapshot,
  X7PushableBlockSnapshot,
  X8BlockSnapshot
} from '../../../domain';
import {Disposers, HttpPromiseState} from '../../../lib/helpers';
import {Classroom} from '../models/classroom-models';
import {ClassroomUtils} from '../models/classroom-utils';
import {ClassroomLoaders} from '../stores/classroom-loaders';
import {ClassroomHubStore} from '../stores/ClassroomHubStore';


function canShowBlock(block: X8BlockSnapshot, revealedBlockIds: ReadonlySet<number>): boolean {
  if (block.BlockType === 'Visible')
    return true;
  if (block.BlockType === 'Hidden')
    return false;
  return revealedBlockIds.has(block.Id);
}

export class StudentClassroomSceneStore {
  @observable.ref
  classroomLoader: HttpPromiseState<Classroom> | null;

  hubStore: ClassroomHubStore;

  @observable.ref
  snapshotState: HttpPromiseState<X5LessonSnapshot> | null;
  
  @observable.ref
  snapshot: X5LessonSnapshot | null;

  @observable
  lessonType: string | undefined;

  private readonly disposers = new Disposers();

  constructor(readonly classroomId: number) {
    makeObservable(this);
    this.classroomLoader = null;

    this.hubStore = new ClassroomHubStore(classroomId, 'Student', false);
    this.disposers.add(this.hubStore);

    this.snapshotState = this.snapshot = null;
    this.lessonType = X5LessonTypes[0];
    this.setup();
  }

  private setup() {
    this.disposers.add(
      reaction(
        () => this.classroomLoader?.value,
        this.onClassroomLoaded),
      reaction(
        () => this.snapshotState?.value,
        this.onLessonLoaded),
    );
  }

  @action
  loadClassroom = () => {
    if (this.classroomLoader?.isPending)
      return;
    this.classroomLoader = ClassroomLoaders.Student.getClassroom(this.classroomId);
  };

  @action
  private setLessonType = () => {
    this.lessonType = this.classroomLoader?.value?.Lesson?.LessonType
  }

  @action
  private onClassroomLoaded = () => {
    const state = this.classroomLoader;
    if (state === null || !state.isResolved || state.value.Status !== 'InProgress')
      return;
    this.hubStore.start();
    this.setLessonType();
    this.loadLesson();
  };

  @computed
  get canConnectToHub(): boolean {
    if (this.classroomLoader?.value?.Status !== 'InProgress')
      return false;
    return (!this.hubStore.connection.dontReconnect);
  }

  @action
  loadLesson = () => {
    if (this.snapshotState?.isPending === true || this.snapshotState?.isCompleted === true)
      return;
    this.snapshotState = ClassroomLoaders.Student.getLessonSnapshot(this.classroomId);
  };

  @action
  private onLessonLoaded = () => {
    const state = this.snapshotState;
    if (state === null || !state.isResolved)
      return;
    this.snapshot = ClassroomUtils.prepareLesson(state.value);
  };

  @computed
  get currentPage(): X6PageSnapshot | null {
    const snapshot = this.snapshot!;
    const classroomState = this.hubStore.state.classroomState!;
    if (snapshot === null || classroomState === null)
      return null;

    const pageId = classroomState.CurrentPageId;
    if (pageId === null)
      return null;

    const page = snapshot.PageSnapshots!
      .find(x => x.Id === pageId);
    if (page === undefined)
      return null;
    return page;
  }

  @computed
  get currentPushableBlock(): X7PushableBlockSnapshot | null {
    const page = this.currentPage!;
    const classroomState = this.hubStore.state.classroomState!;
    if (page === null || classroomState === null)
      return null;

    const pushableBlockId = classroomState.CurrentPushableBlockId;
    if (pushableBlockId === null)
      return null;

    const pushableBlock = page.PushableBlockSnapshots!
      .find(x => x.Id === pushableBlockId);
    if (pushableBlock === undefined)
      return null;
    return pushableBlock;
  }

  @computed
  get currentVisibleBlocks(): X8BlockSnapshot[] {
    const pushableBlock = this.currentPushableBlock!;
    if (pushableBlock === null)
      return [];
    const revealedBlockIds = this.hubStore.revealedBlockIds;
    return pushableBlock.BlockSnapshots!
      .filter(block => canShowBlock(block, revealedBlockIds));
  }

  dispose() {
    this.disposers.dispose();
  }
}
